<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Report Content Area (Scrollable) -->
    <div class="flex-1 overflow-hidden min-h-0">
      <!-- Loading State -->
      <report-loading
        v-if="isLoading"
        :loading="isLoading"
        :type="loadingType"
        :variant="loadingVariant"
        :message="loadingMessage"
        :rows="loadingRows"
        :columns="loadingColumns"
      />

      <!-- Empty State -->
      <div
        v-else-if="!reportData || (Array.isArray(reportData) && reportData.length === 0) || (typeof reportData === 'object' && !Array.isArray(reportData) && (!reportData.periods || reportData.periods.length === 0))"
        class="flex-1 flex items-center justify-center"
      >
        <div class="text-center py-12">
          <v-icon size="64" color="grey-lighten-1" class="mb-4"
            >mdi-database-off</v-icon
          >
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ $t("reports.empty.title") }}
          </h3>
          <p class="text-gray-500 mb-4">
            {{ $t("reports.empty.description") }}
          </p>
          <button
            @click="handleRefresh"
            :disabled="isLoading"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <v-icon
              size="small"
              class="mr-2"
              :class="{ 'animate-spin': isLoading }"
              >mdi-refresh</v-icon
            >
            {{
              isLoading
                ? $t("reports.loading.processing")
                : $t("reports.empty.refresh")
            }}
          </button>
        </div>
      </div>

      <!-- Report Table Container -->
      <div v-else class="h-full flex flex-col w-full">
        <!-- Table Wrapper with Scrollable Body -->
        <div class="flex-1 overflow-hidden w-full">
          <slot
            name="table"
            :data="reportData"
            :total-count="totalCount"
            :current-page="currentPage"
            :records-per-page="recordsPerPage"
          />
        </div>
      </div>
    </div>

    <!-- Pagination Footer (Always Visible) -->
    <div v-if="supportsPagination" class="flex-shrink-0 bg-white border-t border-gray-200 shadow-sm">
      <div class="px-3 py-2">
        <report-pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          :total-count="totalCount"
          :records-per-page="recordsPerPage"
          :available-limits="availableLimits"
          :show-records-per-page="true"
          :show-pagination-info="true"
          :show-jump-to-page="false"
          :is-loading="isLoading"
          @page-change="handlePageChange"
          @records-per-page-change="handleRecordsPerPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ReportLoading from "./ReportLoading.vue";
import ReportPagination from "./ReportPagination.vue";

export default {
  name: "BaseReport",
  components: {
    ReportLoading,
    ReportPagination,
  },
  props: {
    // Data props
    reportData: {
      type: [Array, Object],
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },

    // Pagination props
    currentPage: {
      type: Number,
      required: true,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    recordsPerPage: {
      type: Number,
      required: true,
    },
    availableLimits: {
      type: Array,
      default: () => [10, 25, 50, 100],
    },
    supportsPagination: {
      type: Boolean,
      default: true,
    },

    // Loading props
    isLoading: {
      type: Boolean,
      default: false,
    },
    loadingType: {
      type: String,
      default: "skeleton",
      validator: (value) => ["spinner", "skeleton", "progress"].includes(value),
    },
    loadingVariant: {
      type: String,
      default: "table",
      validator: (value) =>
        ["default", "table", "card", "chart"].includes(value),
    },
    loadingMessage: {
      type: String,
      default: null,
    },
    loadingRows: {
      type: Number,
      default: 5,
    },
    loadingColumns: {
      type: Number,
      default: 4,
    },
  },
  emits: ["page-change", "records-per-page-change", "refresh"],
  methods: {
    handlePageChange(page) {
      this.$emit("page-change", page);
    },

    handleRecordsPerPageChange(limit) {
      console.log("BaseReport: Records per page changed to:", limit);
      this.$emit("records-per-page-change", limit);
    },

    handleRefresh() {
      this.$emit("refresh");
    },
  },
};
</script>

<style scoped>
/* Full height layout */
.h-full {
  height: 100%;
}

/* Ensure proper scrolling behavior */
.overflow-hidden {
  overflow: hidden;
}

.min-h-0 {
  min-height: 0;
}

.flex-1 {
  flex: 1 1 0%;
}

.flex-shrink-0 {
  flex-shrink: 0;
}
</style>
